import axios from 'axios'
import { TokenService } from '../services/storage.service'
import { downloadWorkbook } from './exportWorkbook'
import { shapeCalendar } from './calendarExportData'

// Static contacts for HSP Brands, which is not a cp_clients row and so has no
// contact record returned by the API. Mirrors ItemList.vue.
const HSP_CONTACTS = [
  { name: 'Angela French', phone: '650.418.2016', email: 'tastings@hspbrands.com' }
]

// Item List column order/labels, mirroring ItemList.vue's itemListHeaders.
const ITEM_LIST_HEADERS = [
  'Company', 'Description', 'NORCAL SFWY CIC', 'UPC', 'Demo Conducted By',
  'Invoice Cost', 'Contact Information', 'CIC', 'Size', 'CIC Validation',
  'Description', 'DIST', 'Valid'
]

// Store List column order/labels, matching the source CSV exactly.
const STORE_LIST_HEADERS = [
  'STORE', 'ADDRESS', 'CITY', 'STATE', 'ZIP', 'COUNTY',
  'CROSS ST.', 'AREA CODE', 'PHONE', 'NOTES'
]

// Column widths (character units) for each tab.
const ITEM_LIST_COLS = [28, 50, 16, 16, 18, 10, 40, 12, 12, 12, 40, 8, 8].map(wch => ({ wch }))
const STORE_LIST_COLS = [8, 32, 18, 8, 8, 16, 24, 12, 14, 40].map(wch => ({ wch }))

// Build the Item List sheet, sorted by company ascending (the default view
// sort) so both buttons produce an identical file regardless of live UI sort.
const shapeItemList = (items, legend) => {
  // Map work_order -> ' -1', ' -2', ... for companies with multiple work orders
  const companyWOs = {}
  for (const entry of legend) {
    if (!companyWOs[entry.company]) companyWOs[entry.company] = []
    companyWOs[entry.company].push(entry.work_order)
  }
  const designators = {}
  for (const wos of Object.values(companyWOs)) {
    if (wos.length > 1) wos.forEach((wo, i) => { designators[wo] = ` -${i + 1}` })
  }

  const strVal = (item, k) => (item[k] != null ? String(item[k]) : '').toLowerCase()
  const sorted = [...items].sort((a, b) => {
    const cmp = strVal(a, 'company').localeCompare(strVal(b, 'company'))
    if (cmp !== 0) return cmp
    const da = designators[(a.work_orders || [])[0]] || ''
    const db = designators[(b.work_orders || [])[0]] || ''
    return da.localeCompare(db)
  })

  const contactsFor = item => {
    if (!item.agency) return HSP_CONTACTS
    return item.contact ? [item.contact] : []
  }

  const rows = sorted.map(item => {
    const designator = designators[(item.work_orders || [])[0]] || ''
    const contacts = contactsFor(item)
      .map(c => [c.name, c.phone, c.email].filter(Boolean).join(', '))
      .join('; ')
    return [
      (item.company || '') + designator,
      item.name,
      item.safeway_cic,
      item.upc,
      item.agency || 'HSP Brands',
      item.invoice_cost,
      contacts,
      item.absco_cic,
      item.size,
      item.cic_validation,
      item.absco_description,
      item.dist,
      item.valid
    ]
  })

  return { name: 'Item List', aoa: [ITEM_LIST_HEADERS, ...rows], cols: ITEM_LIST_COLS }
}

// Build the Store List sheet. The endpoint already returns export-ready rows
// (state abbreviated, phone split into area_code/phone), so this is a passthrough.
const shapeStoreList = storeRows => {
  const rows = storeRows.map(r => [
    r.warehouse_id, r.address, r.city, r.state, r.zipcode,
    r.county, r.cross_st, r.area_code, r.phone, r.notes
  ])
  return { name: 'Store List', aoa: [STORE_LIST_HEADERS, ...rows], cols: STORE_LIST_COLS }
}

// Fetch the datasets fresh and download a single .xlsx with the tabs in order:
// Calendar, Item List, Store List. Fetching fresh (rather than reusing
// whichever view is mounted) guarantees both buttons produce the same file.
// Retailers without the itemListView priv pass includeItemList false, which
// skips both the item-list request and its tab.
export const downloadCombinedExport = async ({ apiRoot, dateFrom, dateTo, includeItemList = true }) => {
  const code = TokenService.getLoginCode()
  const dateParams = new URLSearchParams({
    access_key: TokenService.getAccessKey(),
    date_start: dateFrom,
    date_end: dateTo
  })
  const keyParam = new URLSearchParams({ access_key: TokenService.getAccessKey() })

  const [calendar, itemList, storeList] = await Promise.all([
    axios.get(`${apiRoot}retail/${code}/calendar?${dateParams}`),
    includeItemList ? axios.get(`${apiRoot}retail/${code}/item-list?${dateParams}`) : null,
    axios.get(`${apiRoot}retail/${code}/store-list?${keyParam}`)
  ])

  const sheets = [
    shapeCalendar(calendar.data.stores || [], calendar.data.events || {}, dateFrom, dateTo)
  ]
  if (itemList) {
    sheets.push(shapeItemList(itemList.data.items || [], itemList.data.legend || []))
  }
  sheets.push(shapeStoreList(storeList.data || []))

  downloadWorkbook(sheets, `TastingEventsCalendar_${dateFrom}_${dateTo}.xlsx`)
}
